import { useCallback, useContext, useEffect, useRef } from 'react';

import { AgGridReact, AgGridReactProps } from 'ag-grid-react';
import { Button } from 'primereact/button';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { InputText } from 'primereact/inputtext';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { WithLocalStorage } from '@/components';
import { useDefaultGridOptions } from '@/grid';
import { defaultColDef } from '@/grid/defaults';
import { PrimaryContext } from '@/services';
import { ShipmentDto } from '@/services/semen-service';
import { useContextMenu } from '@/utils/context-menu';
import {
  ShipmentFilters,
  useShipmentsDataSource,
} from '@/utils/shipments-data-source';

import { shipmentsGridSideBar, useShipmentsColumnDefs } from './column-defs';

const EMPTY_FILTERS: ShipmentFilters = {};

export type ShipmentsGridProps = {
  selectedShipment: ShipmentDto | null;
  setSelectedShipment: (shipment: ShipmentDto | null) => void;
  storageKey?: string;
  title?: string;
  filters?: ShipmentFilters;
  height?: string;
  gridRef?: React.RefObject<AgGridReact>;
  searchInput?: string;
  onSearchInputChange?: (value: string) => void;
  onSearch?: () => void;
  showSearch?: boolean;
  openNewInNewWindow?: boolean;
};

export const ShipmentsGrid = ({
  selectedShipment,
  setSelectedShipment,
  storageKey = 'shipments-grid',
  title,
  filters = {},
  height = '400px',
  gridRef: externalGridRef,
  searchInput = '',
  onSearchInputChange,
  onSearch,
  showSearch = false,
  openNewInNewWindow = false,
}: ShipmentsGridProps) => {
  const { t } = useTranslation();
  const { themeMode } = useContext(PrimaryContext);
  const defaultGridOptions = useDefaultGridOptions();
  const internalGridRef = useRef<AgGridReact>(null);
  const gridRef = externalGridRef || internalGridRef;

  const columnDefs = useShipmentsColumnDefs();

  const { dataSource } = useShipmentsDataSource(filters ?? EMPTY_FILTERS);

  const { getContextMenuItems: shipmentsContextMenu } = useContextMenu({
    idKey: 'id',
  });

  const refreshGrid = useCallback(() => {
    if (!gridRef.current?.api) return;
    gridRef.current.api.setGridOption('serverSideDatasource', dataSource);
  }, [dataSource, gridRef]);

  useEffect(() => {
    refreshGrid();
  }, [refreshGrid]);

  const onSelectionChanged = useCallback(() => {
    const selectedRows = gridRef.current?.api?.getSelectedRows() as
      | ShipmentDto[]
      | undefined;
    const shipment =
      selectedRows && selectedRows.length > 0 ? selectedRows[0] : null;
    if (shipment?.id !== selectedShipment?.id) {
      setSelectedShipment(shipment);
    }
  }, [gridRef, setSelectedShipment, selectedShipment]);

  const onRowDoubleClicked = (event: any) => {
    const shipment = event.data;
    if (shipment?.id) {
      window.open(`/shipping/shipment?id=${shipment.id}`, '_blank');
    }
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      onSearch?.();
    }
  };

  return (
    <div className="flex min-h-0 flex-col">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            {title || t('shipping.shipments')}
          </h3>
        </div>
        <div className="flex gap-2">
          {showSearch && (
            <>
              <IconField iconPosition="left" className="flex-1">
                <InputIcon className="pi pi-search" />
                <InputText
                  value={searchInput}
                  onChange={(e) => onSearchInputChange?.(e.target.value)}
                  onKeyDown={handleSearchKeyPress}
                  placeholder={t('common.search')}
                  className="w-full"
                />
              </IconField>
              <Button
                icon="pi pi-search"
                onClick={onSearch}
                tooltip={t('common.search')}
                tooltipOptions={{ position: 'top' }}
              />
            </>
          )}
          {selectedShipment?.id ? (
            <Link
              to={`/shipping/shipment?id=${selectedShipment.id}`}
              className="p-button p-button-sm p-button-secondary flex items-center gap-2"
              title={t('common.edit')}
              style={{ textDecoration: 'none' }}
            >
              <span className="pi pi-pencil" />
              {t('common.edit')}
            </Link>
          ) : (
            <span
              className="p-button p-button-sm p-button-secondary flex cursor-not-allowed items-center gap-2 opacity-50"
              title={t('common.edit')}
              aria-disabled="true"
            >
              <span className="pi pi-pencil" />
              {t('common.edit')}
            </span>
          )}
          <Link
            to="/create-shipment"
            target={openNewInNewWindow ? '_blank' : undefined}
            rel={openNewInNewWindow ? 'noopener noreferrer' : undefined}
            className="p-button p-button-sm p-button-success flex items-center gap-2"
            title={t('common.create')}
            style={{ textDecoration: 'none' }}
          >
            <span className="pi pi-plus" />
            {t('common.new')}
          </Link>
        </div>
      </div>
      <div style={{ height }} className="min-h-0">
        <WithLocalStorage storageKey={storageKey}>
          <AgGridReact
            gridOptions={defaultGridOptions}
            ref={gridRef}
            className={`ag-theme-transova${themeMode === 'dark' ? '-dark' : ''} h-full`}
            columnDefs={columnDefs as AgGridReactProps['columnDefs']}
            onSelectionChanged={onSelectionChanged}
            onFilterChanged={refreshGrid}
            onGridReady={refreshGrid}
            onRowDoubleClicked={onRowDoubleClicked}
            rowSelection="single"
            suppressRowClickSelection={true}
            defaultColDef={defaultColDef}
            rowModelType="serverSide"
            cacheBlockSize={20}
            maxBlocksInCache={5}
            sideBar={shipmentsGridSideBar}
            getContextMenuItems={shipmentsContextMenu}
          />
        </WithLocalStorage>
      </div>
    </div>
  );
};
